import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ACTIVE_EMPLOYEE_STATUSES, getEmployees } from '@/lib/employees';
import { cn } from '@/lib/utils';
import { Search, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
}

interface EmployeeMultiSelectProps {
  value: EmployeeOption[];
  onChange: (next: EmployeeOption[]) => void;
  placeholder?: string;
  /** Extra employee IDs to filter out from search results (e.g. people already selected for another role). */
  excludeIds?: string[];
  /** Open the dropdown immediately on mount — used by inline edit flows where the picker mounts already-focused. */
  autoFocus?: boolean;
  ariaInvalid?: boolean;
  /** Fires when the input loses focus, after the dropdown closes — lets parents trigger autosave on blur. */
  onBlur?: () => void;
}

/**
 * The box that clips the picker's list inside a dialog: its closest ancestor, up to and
 * including the dialog, whose vertical overflow is auto or scroll. Null outside a dialog,
 * and when nothing in it scrolls (an overflow-hidden panel the user could not scroll back);
 * the list then opens downwards as it always did, and is not scrolled into view.
 */
function dialogScrollBox(el: HTMLElement): HTMLElement | null {
  const dialog = el.closest('[role="dialog"], [role="alertdialog"]');
  if (!dialog) return null;
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    if (node === dialog) return null;
  }
  return null;
}

/** Room above and below `anchor` inside `box`'s visible area (within its borders). */
function roomAround(box: HTMLElement, anchor: HTMLElement) {
  const top = box.getBoundingClientRect().top + box.clientTop;
  const bottom = top + box.clientHeight;
  const field = anchor.getBoundingClientRect();
  return { above: field.top - top, below: bottom - field.bottom };
}

/**
 * The room the list takes at its tallest: its max-height (max-h-40, borders included, in
 * the user's own rem) plus its 4px gap from the field (mt-1 / mb-1).
 */
function listMaxSpace(list: HTMLElement) {
  const maxHeight = parseFloat(getComputedStyle(list).maxHeight);
  return (Number.isFinite(maxHeight) ? maxHeight : 160) + 4;
}

export function EmployeeMultiSelect({
  value,
  onChange,
  placeholder = 'Search people...',
  excludeIds,
  autoFocus = false,
  ariaInvalid = false,
  onBlur,
}: EmployeeMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<EmployeeOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const cramped = useRef(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownVisible = showDropdown && results.length > 0;

  // The list is drawn inside the picker rather than portalled, so inside a dialog —
  // which scrolls itself and clips what overflows it — a list opened near the bottom
  // edge was cut off. When it opens, before it is painted, pick its side once for its
  // tallest size, so it does not jump sides as a search changes its length: downwards
  // if that fits below the field, upwards if it fits above, otherwise downwards with
  // the dialog scrolled to show it. Outside a dialog it always opens downwards, as before.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const list = dropdownRef.current;
    if (!dropdownVisible || !anchor || !list) return;
    const box = dialogScrollBox(anchor);
    const room = box ? roomAround(box, anchor) : null;
    const need = listMaxSpace(list);
    cramped.current = !!room && room.below < need && room.above < need;
    setOpenUp(!!room && room.below < need && room.above >= need);
  }, [dropdownVisible]);

  // Only when there is room on neither side: scroll the dialog just enough to show the
  // list as it is now, again whenever a new search makes it longer.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const list = dropdownRef.current;
    if (!dropdownVisible || openUp || !cramped.current || !anchor || !list) return;
    const box = dialogScrollBox(anchor);
    if (!box) return;
    const overshoot = list.offsetHeight + 4 - roomAround(box, anchor).below;
    if (overshoot > 0) box.scrollTop += overshoot;
  }, [dropdownVisible, openUp, results.length]);

  useEffect(() => {
    if (search.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await getEmployees({
          search,
          limit: 10,
          statuses: ACTIVE_EMPLOYEE_STATUSES,
        });
        const exclude = new Set<string>([
          ...value.map((v) => v.id),
          ...(excludeIds ?? []),
        ]);
        setResults(
          res.data
            .filter((e: { id: string }) => !exclude.has(e.id))
            .map((e: EmployeeOption) => ({
              id: e.id,
              firstName: e.firstName,
              lastName: e.lastName,
              avatarUrl: e.avatarUrl,
            })),
        );
      } catch {
        /* ignore */
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, value, excludeIds]);

  return (
    <div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((e) => (
            <Badge
              key={e.id}
              variant="secondary"
              className="max-w-[220px] gap-1 py-1"
              title={`${e.firstName} ${e.lastName}`}
            >
              <span className="h-5 w-5 shrink-0 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px] font-medium">
                {e.firstName[0]}
                {e.lastName[0]}
              </span>
              <span className="truncate">
                {e.firstName} {e.lastName}
              </span>
              <X
                className="h-3 w-3 shrink-0 cursor-pointer"
                onClick={() => onChange(value.filter((v) => v.id !== e.id))}
              />
            </Badge>
          ))}
        </div>
      )}
      <div ref={anchorRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={search}
            autoFocus={autoFocus}
            aria-invalid={ariaInvalid}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => {
              // Defer hide so onMouseDown on a dropdown item runs first.
              setTimeout(() => {
                setShowDropdown(false);
                onBlur?.();
              }, 200);
            }}
            className="pl-9"
          />
        </div>
        {dropdownVisible && (
          <div
            ref={dropdownRef}
            className={cn(
              'absolute z-10 w-full bg-popover border rounded-md shadow-md max-h-40 overflow-auto',
              openUp ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            {results.map((e) => (
              <button
                key={e.id}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  onChange([...value, e]);
                  setSearch('');
                  setShowDropdown(false);
                }}
                title={`${e.firstName} ${e.lastName}`}
              >
                <span className="h-6 w-6 shrink-0 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
                  {e.firstName[0]}
                  {e.lastName[0]}
                </span>
                <span className="truncate">
                  {e.firstName} {e.lastName}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
